import { useState, useCallback } from 'react';
import { MySwal } from '../components/ui/MySwal';
import { useDispatch } from 'react-redux';
import { addToast } from '../redux/slices/toastSlice';

/**
 * Custom hook for handling errors with user-friendly messages and recovery options
 */
export const useErrorHandler = () => {
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // เก็บฟังก์ชัน retry ล่าสุดไว้ใน state เพื่อให้ Swal เรียกใช้ได้
  const [lastRetryFn, setLastRetryFn] = useState(null);
  const dispatchEvent = useDispatch();

  const handleError = useCallback((error, context = {}, retryFn = null) => {
    console.error('Error occurred:', error, context);
    
    // Transform technical errors into user-friendly messages
    const userFriendlyError = transformError(error, context);
    const getSweetAlertIcon = (type) => {
      switch (type) {
        case 'network':
          return 'error';
        case 'file':
          return 'warning';
        case 'validation':
          return 'info';
        case 'permission':
          return 'error';
        case 'server':
          return 'error';
        default:
          return 'error';
      }
    }

    // เก็บฟังก์ชัน retry ถ้ามี
    if (retryFn) setLastRetryFn(() => retryFn);
    
    setError({
      ...userFriendlyError,
      originalError: error,
      context,
      timestamp: new Date().toISOString(),
      id: Date.now().toString(36) + Math.random().toString(36).substr(2)
    });

    if (userFriendlyError.recoverable && retryFn) {
      MySwal.fire({
        title: userFriendlyError.title,
        text: userFriendlyError.message,
        icon: getSweetAlertIcon(userFriendlyError.type),
        showCancelButton: true,
        confirmButtonText: 'Try Again',
        cancelButtonText: 'Close',
      }).then((result) => {
        if (result.isConfirmed) {
          // เรียกใช้ฟังก์ชัน retry ที่ส่งเข้ามา
          retry(retryFn);
        }
      });
    } else {
      // แสดง Toast สำหรับ error ที่ไม่สามารถ retry ได้ หรือไม่มีฟังก์ชัน retry
      const toastMessage = context ? context.message : userFriendlyError.message;
      dispatchEvent(addToast({
          id: Date.now().toString(),
          message: toastMessage,
          type: getSweetAlertIcon(userFriendlyError.type) || 'error'
        }
      ));
    }
  }, [addToast]);

  const clearError = useCallback(() => {
    setError(null);
    setIsRetrying(false);
  }, []);

  const retry = useCallback(async (retryFn) => {
    if (!retryFn) return;
    
    setIsRetrying(true);
    try {
      await retryFn();
      clearError();
    } catch (retryError) {
      handleError(retryError, { isRetry: true }, retryFn); // ส่ง retryFn ไปด้วยเผื่อจะ retry ซ้อน
    } finally {
      setIsRetrying(false);
    }
  }, [handleError, clearError]);

  return {
    error,
    isRetrying,
    handleError,
    clearError,
    retry: lastRetryFn ? () => retry(lastRetryFn) : null // ทำให้ retry จากภายนอกยังใช้ได้
  };
};

/**
 * Transform technical errors into user-friendly messages
 */
const transformError = (error, context = {}) => {
  // Network errors
  if (error.name === 'NetworkError' || error.message?.includes('fetch')) {
    return {
      title: 'Connection Problem',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      type: 'network',
      recoverable: true
    };
  }

  // File processing errors
  if (context.operation === 'file-upload' || error.message?.includes('file')) {
    return {
      title: 'File Processing Error',
      message: 'There was a problem processing your file. Please check the file format and try again.',
      type: 'file',
      recoverable: true
    };
  }

  // Validation errors
  if (context.operation === 'validation' || error.message?.includes('validation')) {
    return {
      title: 'Validation Error',
      message: 'Unable to validate the data. Please check your input and try again.',
      type: 'validation',
      recoverable: true
    };
  }

  // Permission errors
  if (error.status === 403 || error.message?.includes('permission')) {
    return {
      title: 'Access Denied',
      message: 'You don\'t have permission to perform this action. Please contact your administrator.',
      type: 'permission',
      recoverable: false
    };
  }

  // Server errors
  if (error.status >= 500) {
    return {
      title: 'Server Error',
      message: 'Our servers are experiencing issues. Please try again in a few minutes.',
      type: 'server',
      recoverable: true
    };
  }

  // Generic error
  return {
    title: 'Unexpected Error',
    message: error.message || 'Something went wrong. Please try again.',
    type: 'generic',
    recoverable: true
  };
};

/**
 * Hook for handling async operations with error handling
 */
export const useAsyncOperation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError, clearError, retry } = useErrorHandler();

  const execute = useCallback(async (asyncFn, context = {}) => {
    setIsLoading(true);
    clearError();
    
    try {
      const result = await asyncFn();
      return result;
    } catch (error) {
      handleError(error, context, () => execute(asyncFn, context));
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleError, clearError]);

  return {
    isLoading,
    error,
    execute,
    retry,
    clearError
  };
};